/* eslint-disable no-param-reassign */
/* global G */
import { v4 as uuidV4 } from 'uuid'
import sequenceModelSubmit from 'lib/sequence/model/api/submit'
import modelCreate from 'lib/sequence/model/api/create'
import modelTransform from 'lib/sequence/model/transformer'
import showToast, { ToastOptions } from 'app/_shared/component/showToast'

const descriptor = Symbol('hook::model::submit').toString()

type SubmitEventHandlerOptions = {
  /** translations' namespace */
  ns?: string,
  /** translation's key part */
  type?: string,
  /** handler called if there is a conflict when attempting to update the item */
  onConflict?: (obj: Gaia.AppModule.Spec) => <T>(...args: T[]) => Promise<T[]>,
  /** handler called if the item to update had been deleted */
  onDeleted?: (obj: Gaia.AppModule.Spec) => <T>(...args: T[]) => Promise<T[]>,
}

/**
 * Returns options for the success submit toast.
 *
 * @param options       configuration options
 * @param options.ns    translations' namespace
 * @param options.type  model type name
 */
const successSubmitToastOptions = ({ ns = 'common', type = 'model' }): ToastOptions => ({
  severity: 'success',
  message: {
    ns,
    key: `toast.${type}.submitSuccess`,
    defaultValue: 'Item submitted',
  },
})

/**
 * Returns options for the success (local) save toast.
 *
 * @param options       configuration options
 * @param options.ns    translations' namespace
 * @param options.type  model type name
 */
const successSaveToastOptions = ({ ns = 'common', type = 'model' }): ToastOptions => ({
  duration: 6000,
  severity: 'warning',
  message: {
    ns,
    key: `toast.${type}.saveSuccess.message`,
    defaultValue: `${type} saved on this device`,
  },
  description: {
    ns,
    key: `toast.${type}.saveSuccess.description`,
    defaultValue: 'No connection right now. It will be submitted automatically once you\'re back online.',
  },
})

/**
 * Returns options for the conflict error toast.
 *
 * @param options       configuration options
 * @param options.ns    translations' namespace
 * @param options.type  model type name
 */
const conflictToastOptions = ({ ns = 'common', type = 'model' }): ToastOptions => ({
  severity: 'error',
  message: {
    ns,
    key: `toast.${type}.conflictError`,
    defaultValue: `Sorry, the ${type} was updated by another user. Please, repeat your input.`,
  }
})

/**
 * Returns options for the deleted error toast.
 *
 * @param options       configuration options
 * @param options.ns    translations' namespace
 * @param options.type  model type name
 */
const deletedToastOptions = ({ ns = 'common', type = 'model' }): ToastOptions => ({
  severity: 'error',
  message: {
    ns,
    key: `toast.${type}.deletedError`,
    defaultValue: `Sorry, the ${type} was deleted by another user.`,
  }
})

/**
 * Creates a new {@link Draft} object from `model`.
 *
 * @param model model to create a draft of
 */
const createDraft = (model: Gaia.Model.Spec) => {
  const { api: type } = model[G.PROPS]
  const isRemote = !!model[G.CACHE]?._rev
  const key = model[G.STATE][G.REF] || uuidV4()

  modelCreate(model)
  modelTransform(model)

  const data = model[G.STATE][G.DATA]

  return {
    id: key,
    revision: '0',
    remoteRevision: '0',
    data: {
      ...data,
      key,
    },
    type,
    action: isRemote ? 'update' : 'create',
    save: true,
    submit: true,
    error: null,
  }
}

/**
 * Model Submit Attribute Hook.
 *
 * Executes model attribute data remote or local submission, based on model attribute cache and
 * reference.
 *
 * If `attributeName` is `null`, attempts to submit the main model.
 *
 * @param attributeName name of the model attribute to submit
 * @param [options]     handler configuration object
 */
const submitAttributeWithDrafts = (
  attributeName: string | null,
  options: SubmitEventHandlerOptions = {},
) => (
  obj: Gaia.AppModule.Spec,
) => async (
  ...args: any[]
) => {
  const { drafts } = obj[G.ADAPTER][G.PERSISTENCE]
  const online = obj[G.ADAPTER][G.SESSION][G.STATE][G.ONLINE]
  const { onConflict, onDeleted } = options

  const model = attributeName ? obj[G.MODEL][G.CHILDREN][attributeName] : obj[G.MODEL]
  const httpAdapter = model[G.ADAPTER][G.HTTP]
  const isRemote = !!model[G.CACHE]?._rev
  const id = model[G.STATE][G.REF]

  let error: any
  let conflict = false
  let deleted = false

  if (online) {

    try {
      const context = { middleware: ({ error, ...middleware }) => Object.values(middleware) }
      await httpAdapter.withContext(context, async () => {
        // if object is not yet remote, we have to set its _id parameter for it to keep the same id
        // we also remove it's [G.STATE][G.REF] value so that `sequenceModelSubmit` attempt to
        // create instead of updating it
        if (!isRemote) {
          model[G.STATE][G.REF] = null
          model[G.DATA]._id = id
        }
        await sequenceModelSubmit(model)
        await showToast(obj, successSubmitToastOptions(options))
      })
      // if there was a draft and the model was successfully submitted, we remove it
      id && await drafts.delete(id)
    } catch (e: any) {
      console.log(`${model._name} ${descriptor} - ${e.message}`)

      conflict = e.code === 409
      deleted = e.code === 404
      error = e
    } finally {
      model[G.STATE][G.REF] = id
    }
  }

  if (conflict) { // if there was an update conflict...

    id && drafts.delete(id) // ...we still delete the local draft
    await showToast(obj, conflictToastOptions(options))
    await onConflict?.(obj)(...args)

  } else if (deleted) { // if the item was deleted...

    id && drafts.delete(id) // ...we also delete the local draft...
    await showToast(obj, deletedToastOptions(options))
    await onDeleted?.(obj)(...args)

  } else if (!online || error) { // for any other kind of error, or if there is no internet...
    const draft = createDraft(model) // ...we create a draft...

    try {
      await drafts.put(draft) // ...and save it on device

      await showToast(obj, successSaveToastOptions(options))

    } catch (e: any) {
      throw Error(`${model._name} ${descriptor} - ${e.message}`, { cause: e })
    }
  }

  return args
}


/**
 * Model Submit Hook.
 *
 * Executes model data remote or local submission, based on model cache and reference.
 *
 * @param options
 */
const submitWithDrafts = (
  options?: SubmitEventHandlerOptions,
) => (
  obj: Gaia.AppModule.Spec,
) => async (
  ...args: any[]
) => {
  return await submitAttributeWithDrafts(null, options)(obj)(...args)
}

export {
  submitWithDrafts as default,
  submitAttributeWithDrafts
}
